import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { showSuccessToast, showErrorToast } from "../../utils/notificationUtils";
import "../../styles/clinicsetup.css";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

const AcceptClinicInvitation = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";

  const buildInitialForm = useMemo(
    () => ({
      email: "",
      firstName: "",
      lastName: "",
      password: "",
      confirmPassword: "",
      phoneNumber: ""
    }),
    []
  );

  const [formData, setFormData] = useState(buildInitialForm);
  const [invitationInfo, setInvitationInfo] = useState({
    clinicName: "",
    invitationMessage: "",
    inviterName: "",
    token: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isValidatingToken, setIsValidatingToken] = useState(true);
  const [errors, setErrors] = useState({});
  const { token } = useParams();

  useEffect(() => {
    if (!token) {
      showErrorToast(t("clinicInvitation.toasts.invalidLink.message"), {
        title: t("clinicInvitation.toasts.invalidLink.title"),
        duration: 5000
      });
      navigate("/login");
      return;
    }

    validateInvitationToken(token);
  }, [navigate, token, t]);

  const validateInvitationToken = async (tokenValue) => {
    setIsValidatingToken(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/clinic-invitations/validate/${tokenValue}`, {
        method: "GET",
        headers: {
          Accept: "application/json"
        }
      });

      const result = await response.json();

      if (response.ok && result.status === "success") {
        const clinicName =
          result.data.clinic.name_en ||
          result.data.clinic.name_ar ||
          t("clinicInvitation.invitation.unknownClinic");

        setInvitationInfo({
          clinicName,
          invitationMessage: result.data.invitation.message || "",
          inviterName: result.data.inviter.name || t("clinicInvitation.invitation.unknownInviter"),
          token: tokenValue
        });

        if (result.data.invitation.email) {
          setFormData((prev) => ({ ...prev, email: result.data.invitation.email }));
        }
      } else {
        const errorMessage = result.message || t("clinicInvitation.toasts.invalidInvitation.message");
        showErrorToast(errorMessage, {
          title: t("clinicInvitation.toasts.invalidInvitation.title"),
          duration: 5000
        });
        navigate("/login");
      }
    } catch (error) {
      showErrorToast(t("clinicInvitation.toasts.validationError.message"), {
        title: t("clinicInvitation.toasts.validationError.title"),
        duration: 5000
      });
      navigate("/login");
    } finally {
      setIsValidatingToken(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = t("clinicInvitation.form.errors.emailRequired");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t("clinicInvitation.form.errors.emailInvalid");
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = t("clinicInvitation.form.errors.firstNameRequired");
    } else if (formData.firstName.trim().length > 255) {
      newErrors.firstName = t("clinicInvitation.form.errors.firstNameLength");
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = t("clinicInvitation.form.errors.lastNameRequired");
    } else if (formData.lastName.trim().length > 255) {
      newErrors.lastName = t("clinicInvitation.form.errors.lastNameLength");
    }

    if (!formData.password) {
      newErrors.password = t("clinicInvitation.form.errors.passwordRequired");
    } else if (formData.password.length < 8) {
      newErrors.password = t("clinicInvitation.form.errors.passwordLength");
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t("clinicInvitation.form.errors.confirmPasswordRequired");
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t("clinicInvitation.form.errors.passwordMismatch");
    }

    if (formData.phoneNumber && formData.phoneNumber.length > 20) {
      newErrors.phoneNumber = t("clinicInvitation.form.errors.phoneLength");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let finalValue = value;
    if (name === "phoneNumber") {
      finalValue = value.replace(/[^0-9\+\-\s]/g, "");
    }
    setFormData((prev) => ({ ...prev, [name]: finalValue }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/clinic-moderators/register-from-invitation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify({
          token: invitationInfo.token,
          email: formData.email.trim(),
          language_preference: i18n.language,
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          password: formData.password,
          password_confirmation: formData.confirmPassword,
          phone_number: formData.phoneNumber.trim() || null
        })
      });

      const result = await response.json();

      if (response.ok && result.status === "success") {
        showSuccessToast(t("clinicInvitation.toasts.success.message"), {
          title: t("clinicInvitation.toasts.success.title"),
          duration: 5000
        });

        localStorage.setItem("access_token", result.data.access_token);
        localStorage.setItem("user_data", JSON.stringify(result.data.user));

        navigate("/medical/clinic/appointment-management/appointments");
      } else {
        const errorMessage = result.message || t("clinicInvitation.toasts.acceptanceFailed.message");
        showErrorToast(errorMessage, {
          title: t("clinicInvitation.toasts.acceptanceFailed.title"),
          duration: 5000
        });
      }
    } catch (error) {
      showErrorToast(t("clinicInvitation.toasts.genericError.message"), {
        title: t("clinicInvitation.toasts.genericError.title"),
        duration: 5000
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidatingToken) {
    return (
      <div className="clinicsetup-page-wrapper">
        <div
          className="clinicsetup-container"
          style={{ textAlign: "center", padding: "60px 20px" }}
          dir={i18n.dir()}
        >
          <div
            style={{
              width: "50px",
              height: "50px",
              border: "4px solid #f3f3f3",
              borderTop: "4px solid #00d4b3",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 20px"
            }}
          />
          <h2 style={{ color: "#333", marginBottom: "10px" }}>
            {t("clinicInvitation.loading.title")}
          </h2>
          <p style={{ color: "#666" }}>
            {t("clinicInvitation.loading.subtitle")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="clinicsetup-page-wrapper">
      <div
        className="clinicsetup-container"
        style={{ maxWidth: "600px", margin: "0 auto" }}
        dir={i18n.dir()}
      >
        <div style={{ textAlign: "center", marginBottom: "40px" }}>
          <h1 style={{
            fontSize: "28px",
            fontWeight: "600",
            color: "#1F2937",
            marginBottom: "10px",
            fontFamily: '"Red Hat Display", sans-serif'
          }}>
            {t("clinicInvitation.header.title")}
          </h1>
          <p style={{
            fontSize: "16px",
            color: "#6B7280",
            marginBottom: "20px",
            fontFamily: '"Red Hat Display", sans-serif'
          }}>
            {t("clinicInvitation.header.subtitle", {
              clinic: invitationInfo.clinicName || t("clinicInvitation.invitation.unknownClinic")
            })}
          </p>
        </div>

        {/* Invitation Information */}
        <div style={{
          backgroundColor: "#F8FAFC",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "20px",
          marginBottom: "30px"
        }}>
          <h3 style={{
            fontSize: "18px",
            fontWeight: "600",
            color: "#1F2937",
            marginBottom: "15px",
            fontFamily: '"Red Hat Display", sans-serif'
          }}>
            {t("clinicInvitation.invitation.title")}
          </h3>
          <div style={{ marginBottom: "10px" }}>
            <strong style={{ color: "#374151" }}>{t("clinicInvitation.invitation.labels.clinic")}:</strong>{" "}
            {invitationInfo.clinicName || t("clinicInvitation.invitation.unknownClinic")}
          </div>
          <div style={{ marginBottom: "10px" }}>
            <strong style={{ color: "#374151" }}>{t("clinicInvitation.invitation.labels.invitedBy")}:</strong>{" "}
            {invitationInfo.inviterName || t("clinicInvitation.invitation.unknownInviter")}
          </div>
          {invitationInfo.invitationMessage && (
            <div>
              <strong style={{ color: "#374151" }}>{t("clinicInvitation.invitation.labels.message")}:</strong>
              <p style={{ 
                margin: "5px 0 0 0", 
                color: "#6B7280", 
                fontStyle: "italic",
                lineHeight: "1.5"
              }}>
                {`"${invitationInfo.invitationMessage}"`}
              </p>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Email Field (Read-only) */}
          <div style={{ marginBottom: "20px" }}>
            <label style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "500",
              color: "#374151",
              fontFamily: '"Red Hat Display", sans-serif'
            }}>
              {t("clinicInvitation.form.fields.email.label")}
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              readOnly
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "2px solid #D1D5DB",
                borderRadius: "8px",
                fontSize: "14px",
                fontFamily: '"Red Hat Display", sans-serif',
                backgroundColor: "#F9FAFB",
                color: "#6B7280",
                cursor: "not-allowed"
              }}
            />
            <div style={{
              fontSize: "12px",
              color: "#6B7280",
              marginTop: "4px",
              fontFamily: '"Red Hat Display", sans-serif'
            }}>
              {t("clinicInvitation.form.fields.email.helper")}
            </div>
          </div>

          {/* First Name Field */}
          <div style={{ marginBottom: "20px" }}>
            <label style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "500",
              color: "#374151",
              fontFamily: '"Red Hat Display", sans-serif'
            }}>
              {t("clinicInvitation.form.fields.firstName.label")}
            </label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              placeholder={t("clinicInvitation.form.fields.firstName.placeholder")}
              disabled={isLoading}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: `2px solid ${errors.firstName ? "#EF4444" : "#D1D5DB"}`,
                borderRadius: "8px",
                fontSize: "14px",
                fontFamily: '"Red Hat Display", sans-serif',
                outline: "none",
                transition: "border-color 0.2s ease",
                backgroundColor: isLoading ? "#F9FAFB" : "white"
              }}
              onFocus={(e) => e.target.style.borderColor = "#3B82F6"}
              onBlur={(e) => e.target.style.borderColor = errors.firstName ? "#EF4444" : "#D1D5DB"}
            />
            {errors.firstName && (
              <div style={{
                color: "#EF4444",
                fontSize: "12px",
                marginTop: "4px",
                fontFamily: '"Red Hat Display", sans-serif'
              }}>
                {errors.firstName}
              </div>
            )}
          </div>

          {/* Last Name Field */}
          <div style={{ marginBottom: "20px" }}>
            <label style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "500",
              color: "#374151",
              fontFamily: '"Red Hat Display", sans-serif'
            }}>
              {t("clinicInvitation.form.fields.lastName.label")}
            </label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              placeholder={t("clinicInvitation.form.fields.lastName.placeholder")}
              disabled={isLoading}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: `2px solid ${errors.lastName ? "#EF4444" : "#D1D5DB"}`,
                borderRadius: "8px",
                fontSize: "14px",
                fontFamily: '"Red Hat Display", sans-serif',
                outline: "none",
                transition: "border-color 0.2s ease",
                backgroundColor: isLoading ? "#F9FAFB" : "white"
              }}
              onFocus={(e) => e.target.style.borderColor = "#3B82F6"}
              onBlur={(e) => e.target.style.borderColor = errors.lastName ? "#EF4444" : "#D1D5DB"}
            />
            {errors.lastName && (
              <div style={{
                color: "#EF4444",
                fontSize: "12px",
                marginTop: "4px",
                fontFamily: '"Red Hat Display", sans-serif'
              }}>
                {errors.lastName}
              </div>
            )}
          </div>

          {/* Password Field */}
          <div style={{ marginBottom: "20px" }}>
            <label style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "500",
              color: "#374151",
              fontFamily: '"Red Hat Display", sans-serif'
            }}>
              {t("clinicInvitation.form.fields.password.label")}
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder={t("clinicInvitation.form.fields.password.placeholder")}
              disabled={isLoading}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: `2px solid ${errors.password ? "#EF4444" : "#D1D5DB"}`,
                borderRadius: "8px",
                fontSize: "14px",
                fontFamily: '"Red Hat Display", sans-serif',
                outline: "none",
                transition: "border-color 0.2s ease",
                backgroundColor: isLoading ? "#F9FAFB" : "white"
              }}
              onFocus={(e) => e.target.style.borderColor = "#3B82F6"}
              onBlur={(e) => e.target.style.borderColor = errors.password ? "#EF4444" : "#D1D5DB"}
            />
            {errors.password && (
              <div style={{
                color: "#EF4444",
                fontSize: "12px",
                marginTop: "4px",
                fontFamily: '"Red Hat Display", sans-serif'
              }}>
                {errors.password}
              </div>
            )}
          </div>

          {/* Confirm Password Field */}
          <div style={{ marginBottom: "20px" }}>
            <label style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "500",
              color: "#374151",
              fontFamily: '"Red Hat Display", sans-serif'
            }}>
              {t("clinicInvitation.form.fields.confirmPassword.label")}
            </label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              placeholder={t("clinicInvitation.form.fields.confirmPassword.placeholder")}
              disabled={isLoading}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: `2px solid ${errors.confirmPassword ? "#EF4444" : "#D1D5DB"}`,
                borderRadius: "8px",
                fontSize: "14px",
                fontFamily: '"Red Hat Display", sans-serif',
                outline: "none",
                transition: "border-color 0.2s ease",
                backgroundColor: isLoading ? "#F9FAFB" : "white"
              }}
              onFocus={(e) => e.target.style.borderColor = "#3B82F6"}
              onBlur={(e) => e.target.style.borderColor = errors.confirmPassword ? "#EF4444" : "#D1D5DB"}
            />
            {errors.confirmPassword && (
              <div style={{
                color: "#EF4444",
                fontSize: "12px",
                marginTop: "4px",
                fontFamily: '"Red Hat Display", sans-serif'
              }}>
                {errors.confirmPassword}
              </div>
            )}
          </div>

          {/* Phone Number Field (Optional) */}
          <div style={{ marginBottom: "30px" }}>
            <label style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "500",
              color: "#374151",
              fontFamily: '"Red Hat Display", sans-serif'
            }}>
              {t("clinicInvitation.form.fields.phone.label")}
            </label>
            <input
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              placeholder={t("clinicInvitation.form.fields.phone.placeholder")}
              disabled={isLoading}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: `2px solid ${errors.phoneNumber ? "#EF4444" : "#D1D5DB"}`,
                borderRadius: "8px",
                fontSize: "14px",
                fontFamily: '"Red Hat Display", sans-serif',
                outline: "none",
                transition: "border-color 0.2s ease",
                backgroundColor: isLoading ? "#F9FAFB" : "white"
              }}
              onFocus={(e) => e.target.style.borderColor = "#3B82F6"}
              onBlur={(e) => e.target.style.borderColor = errors.phoneNumber ? "#EF4444" : "#D1D5DB"}
            />
            {errors.phoneNumber && (
              <div style={{
                color: "#EF4444",
                fontSize: "12px",
                marginTop: "4px",
                fontFamily: '"Red Hat Display", sans-serif'
              }}>
                {errors.phoneNumber}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: "100%",
              padding: "14px 24px",
              backgroundColor: isLoading ? "#9CA3AF" : "#00d4b3",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "16px",
              fontWeight: "600",
              cursor: isLoading ? "not-allowed" : "pointer",
              fontFamily: '"Red Hat Display", sans-serif',
              transition: "background-color 0.2s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px"
            }}
            onMouseEnter={(e) => !isLoading && (e.target.style.backgroundColor = "#00b8a3")}
            onMouseLeave={(e) => !isLoading && (e.target.style.backgroundColor = "#00d4b3")}
          >
            {isLoading ? (
              <>
                <div style={{
                  width: "20px",
                  height: "20px",
                  border: "2px solid #ffffff",
                  borderTop: "2px solid transparent",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite"
                }} />
                {t("clinicInvitation.form.buttons.submitting")}
              </>
            ) : (
              t("clinicInvitation.form.buttons.submit")
            )}
          </button>
        </form>

        <div style={{
          textAlign: "center",
          marginTop: "30px",
          padding: "20px",
          backgroundColor: "#F8FAFC",
          borderRadius: "8px"
        }}>
          <p style={{
            fontSize: "14px",
            color: "#6B7280",
            margin: 0,
            fontFamily: '"Red Hat Display", sans-serif'
          }}>
            {t("clinicInvitation.footer.notice", {
              clinic: invitationInfo.clinicName || t("clinicInvitation.invitation.unknownClinic")
            })}
          </p>
        </div>
      </div>
      
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AcceptClinicInvitation;
